/**
 * Application Entry Point
 * 
 * Renders the React application into the DOM
 * Sets up React Router for navigation and Clerk authentication
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider, ClerkLoading, ClerkLoaded } from '@clerk/clerk-react';
import App from './App';
import Spinner from './components/Spinner';
import './index.css';

// Clerk Publishable Key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Create root and render app with router and auth
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      appearance={{
        baseTheme: undefined,
        variables: {
          colorPrimary: '#8B2442',
          colorBackground: '#0A0A0A',
          colorInputBackground: '#1A1A1A',
          colorInputText: '#F5F5DC',
          colorText: '#F5F5DC',
          colorTextSecondary: '#C4A35A',
          borderRadius: '0',
          colorDanger: '#DC2626',
        },
        elements: {
          card: 'bg-[#0A0A0A] border border-wine-800/50 shadow-2xl shadow-wine-950/50',
          formButtonPrimary: 'bg-wine-600 hover:bg-wine-500 text-white font-semibold border-0',
          footerActionLink: 'text-[#C4A35A] hover:text-[#D4B36A]',
          headerTitle: 'text-cream font-semibold',
          headerSubtitle: 'text-cream/60',
          socialButtonsBlockButton: 'border-wine-800/50 bg-dark-900 hover:bg-wine-900/30 text-cream',
          socialButtonsIconButton: 'border-wine-800/50 bg-dark-900 hover:bg-wine-900/30',
          formFieldInput: 'bg-dark-900 border-wine-800/50 text-cream focus:border-wine-500/70',
          formFieldLabel: 'text-cream/80',
          dividerLine: 'bg-wine-800/50',
          dividerText: 'text-cream/40',
          identityPreviewText: 'text-cream',
          identityPreviewEditButton: 'text-[#C4A35A] hover:text-[#D4B36A]',
          formFieldInputShowPasswordButton: 'text-cream/60 hover:text-cream',
          otpCodeFieldInput: 'bg-dark-900 border-wine-800/50 text-cream',
          alternativeMethodsBlockButton: 'border-wine-800/50 text-cream hover:bg-wine-900/20',
          userButtonPopoverCard: 'bg-[#0A0A0A] border border-wine-800/50',
          userButtonPopoverActionButton: 'text-cream hover:bg-wine-900/20',
          userButtonPopoverFooter: 'hidden',
          // Spinner/loader styling
          spinner: 'text-wine-500',
          loader: 'text-wine-500',
          loadingScreen: 'bg-[#0A0A0A]',
        }
      }}
    >
      <BrowserRouter>
        {/* Custom Framer Motion loading state for Clerk */}
        <ClerkLoading>
          <div className="fixed inset-0 bg-[#0A0A0A] flex items-center justify-center z-50">
            <div className="text-center">
              {/* Premium branded loading */}
              <div className="mb-6">
                <span className="font-display text-3xl font-semibold bg-gradient-to-r from-wine-400 to-wine-500 bg-clip-text text-transparent">
                  Pleasance
                </span>
                <span className="text-wine-600/70 text-lg font-medium">.app</span>
              </div>
              <div className="relative mb-4">
                <Spinner size="xl" color="wine" className="mx-auto" />
                <div className="absolute inset-0 w-12 h-12 mx-auto border-4 border-[#C4A35A]/20 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
              <p className="text-cream/50 text-xs uppercase tracking-widest font-bold">Authenticating...</p>
            </div>
          </div>
        </ClerkLoading>
        <ClerkLoaded>
          <App />
        </ClerkLoaded>
      </BrowserRouter>
    </ClerkProvider>
  </React.StrictMode>
);
